import { getPptTemplate, getPptTemplateDetail } from '@com/tool-bar/menu/utils';
import i18n from 'i18next';
import {
    ITopic,
} from '@src/slide/application/service/change-template-service/component/template-select-list/template-topic-menu';
import { MetaOfTemplate, SearchResult } from '@src/slide/application/service/change-template-service/index.interface';
import {
    IGNORE_TEMPLATE,
} from '@src/slide/application/service/change-template-service/component/template-select-list/filter.config';
import { fetchTemplateByIdByNonVip } from '@src/slide/application/service/ai-ssistant/ai-generator/fetch-template';
import { convert } from '@tencent/slide-markdown-converter';
import cloneDeep from 'lodash/cloneDeep';
import { IPresentationData } from '@tencent/slide-sdk-model';
import {
    ITemplateMallManager,
    ITemplateManagerService,
    ITemplateCheckResult,
} from '@src/slide/application/service/template-mall-content-manager/interface';
import {
    ITemplateAnalyzerResult,
} from '@src/slide/application/service/template-content-analyzer-service/index.interface';
import { IPresentationModel } from '@src/slide/domain/ui-model/element-modeles/Interface/presentation-model.interface';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';

/**
 * 模版商城/运营商模版数据数据管理，拉取/缓存模版数据、模版数据检查
 */
export default class TemplateMallContentManager implements ITemplateMallManager {
    // 全量的话题
    public topicList: ITopic[] = [];

    // 全量的模版数据
    public detailList: SearchResult[] = [];

    // todo: 考虑根据页面类型增加缓存，用于插入独立页面等功能

    private services: ITemplateManagerService;

    /**
     * 基于约定标准检测模版合法性
     * 这里给出的是一个全量标准检查，不同检查项的组合来构成不同等级的模版标准
     * 例如ai模版只要求背景、主题，那么这两项便构成了ai标准
     * @param presentationData
     */
    public checkTemplateWithStandard = async (presentationData: IPresentationData): Promise<ITemplateCheckResult> => {
        const templateStandard: ITemplateCheckResult = {
        };

        await this.services.templateContentAnalyzer.promise().then(async (module) => {
            const { templateFormatService } = this.services;
            const uiDataService = await module.createTemplateUiDataService(presentationData);
            const presentationModel = uiDataService.presentation;
            const layoutAnalyzerResult = await module.analyzerNonStandardTemplate({
                templatePresentation: presentationModel,
            });
            const { slideHeight } = presentationModel.props;
            const { slideWidth } = presentationModel.props;
            const hasCover = this.checkTemplateHasCover(layoutAnalyzerResult);
            const hasContents = this.checkTemplateHasContents(layoutAnalyzerResult);
            const hasSection = this.checkTemplateHasSection(layoutAnalyzerResult);
            const hasCtrTitleInCover = this.checkTemplateHasCtrTitleInCover(layoutAnalyzerResult);
            const hasCtrPlaceholderTitleInCover = this.checkHasCtrPlaceholderTitleInCover(layoutAnalyzerResult);
            const isSingleMaster = this.checkIsSingleMaster(uiDataService);
            const isSingleTheme = this.checkIsSingleTheme(uiDataService);
            const isDefaultTheme = templateFormatService.checkTemplateTheme(presentationModel.themeList[0]);
            const hasSubCover = this.checkTemplateHasSubCover(layoutAnalyzerResult);
            const colorInfo = module.getPresentationColorInfo(presentationModel);

            templateStandard.isStandardWidth = slideWidth === 1920 && slideHeight === 1080;
            templateStandard.hasCover = hasCover;
            templateStandard.hasContents = hasContents;
            templateStandard.hasSection = hasSection;
            templateStandard.hasSubCover = hasSubCover;
            templateStandard.hasCtrTitleInCover = hasCtrTitleInCover;
            templateStandard.hasCtrPlaceholderTitleInCover = hasCtrPlaceholderTitleInCover;
            templateStandard.hasCustomThemeColor = !isDefaultTheme;
            templateStandard.isSingleMaster = isSingleMaster;
            templateStandard.isSingleTheme = isSingleTheme;
            templateStandard.hasMatchingTheme = colorInfo.themeColorNumber >= colorInfo.customColorNumber;
            templateStandard.hasBaseBackground = this.checkHasTemplateBackground(layoutAnalyzerResult);
        });

        return templateStandard;
    };

    public setDelegate = (services: ITemplateManagerService) => {
        this.services = services;
    };

    /**
     * 检查这篇模版是否是单主题
     * @param uiDataService
     */
    public checkIsSingleTheme = (uiDataService: IUiDataService): boolean => {
        const { presentation } = uiDataService;
        return presentation.themeList.length === 1;
    };

    /**
     * 检查这篇模版是否是单母版
     * @param uiDataService
     */
    public checkIsSingleMaster = (uiDataService: IUiDataService): boolean => {
        const masterLength = uiDataService.masterModelList.length || 0;
        return masterLength === 1;
    };

    /**
     * 是否有背景/背景类元素
     * @param templateAnalyzerResult
     */
    public checkHasTemplateBackground = (templateAnalyzerResult: ITemplateAnalyzerResult): boolean => {
        const { templateContentBackResult } = templateAnalyzerResult;
        const { fillBackground, firstElement, secondElement, thirdElement } = templateContentBackResult;
        const hasSolidFill = !!(fillBackground?.solidFill && fillBackground.solidFill.color?.srgb_color !== '#ffffff');
        const hasGradFill = (fillBackground?.gradFill?.gsList?.length || 0) >= 2;
        const hasImageFill = !!fillBackground?.image;

        return hasSolidFill || !! firstElement || !!secondElement || !!thirdElement || hasGradFill || hasImageFill;
    };

    /**
     * 检查模版是否有封面页
     * @param templateAnalyzerResult
     */
    public checkTemplateHasCover = (templateAnalyzerResult: ITemplateAnalyzerResult): boolean => {
        const { templateCoverResult } = templateAnalyzerResult;
        return !!templateCoverResult;
    };

    /**
     * 检查模版是否有副标题/钱也
     * @param templateAnalyzerResult
     */
    public checkTemplateHasSubCover = (templateAnalyzerResult: ITemplateAnalyzerResult): boolean => {
        const { templateSubCoverResult } = templateAnalyzerResult;
        return !!templateSubCoverResult;
    };


    /**
     * 检查封面页是否有主标题（推荐）
     * @param templateAnalyzerResult
     */
    public checkTemplateHasCtrTitleInCover = (templateAnalyzerResult: ITemplateAnalyzerResult): boolean => {
        const { templateCoverResult } = templateAnalyzerResult;
        return !!templateCoverResult.ctrTitleLeaf;
    };

    /**
     * 检查封面主标题是否是标题占位符（推荐）
     * @param templateAnalyzerResult
     */
    public checkHasCtrPlaceholderTitleInCover = (templateAnalyzerResult: ITemplateAnalyzerResult): boolean => {
        const { templateCoverResult } = templateAnalyzerResult;
        return !!templateCoverResult.ctrTitleLeaf?.contentBlock.visualBlocks?.[0]?.info?.element?.isCtrTitlePl;
    };

    /**
     * 检查是否有目录页
     * @param templateAnalyzerResult
     */
    public checkTemplateHasContents = (templateAnalyzerResult: ITemplateAnalyzerResult): boolean => {
        const { templateContentsResult } = templateAnalyzerResult;
        return !!templateContentsResult?.titleLeafList?.length;
    };

    /**
     * 检查是否有章节页
     * @param templateAnalyzerResult
     */
    public checkTemplateHasSection = (templateAnalyzerResult: ITemplateAnalyzerResult): boolean => {
        const { templateSectionResult } = templateAnalyzerResult;
        return !!templateSectionResult?.ctrTitleLeaf;
    };

    /**
     * 检查是否有封底
     * @param templateAnalyzerResult
     */
    public checkTemplateHasCoverBack = (templateAnalyzerResult: ITemplateAnalyzerResult): boolean => {
        const { templateBackCoverResult } = templateAnalyzerResult;
        return !!templateBackCoverResult;
    };

    /**
     * 检查是否有标准版式
     * @param templateAnalyzerResult
     */
    public checkTemplateHasStandardLayout = (templateAnalyzerResult: ITemplateAnalyzerResult): {
        hasCoverLayout: boolean;
        hasContentsLayout: boolean;
        hasSectionLayout: boolean;
        hasCoverBackLayout: boolean;
    } => {
        const { templateContentBackResult } = templateAnalyzerResult;
        const presentationModel = templateContentBackResult.pageModel.parent as IPresentationModel;

        // 是否有命名为标题幻灯片的版式
        let hasCoverLayout = false;

        // 是否有命名为目录幻灯片的版式
        let hasContentsLayout = false;

        // 是否有命名为节标题的版式
        let hasSectionLayout = false;

        // 是否有命名为末尾幻灯片的版式
        let hasCoverBackLayout = false;

        presentationModel.layoutList.forEach((layoutModel) => {
            if (layoutModel.name === '标题幻灯片') {
                hasCoverLayout = true;
            }

            if (layoutModel.name === '目录幻灯片') {
                hasContentsLayout = true;
            }

            if (layoutModel.name === '节标题') {
                hasSectionLayout = true;
            }

            if (layoutModel.name === '末尾幻灯片') {
                hasCoverBackLayout = true;
            }
        });

        return {
            hasCoverLayout,
            hasContentsLayout,
            hasSectionLayout,
            hasCoverBackLayout,
        };
    };

    /**
     * 跑一遍模版商城的所有数据，预计执行30分钟，给产品用
     * 加速的话，可以拆分下topic，一个一个跑
     */
    public checkAllTemplate = async () => {
        const topicList = await this.getTopicList();
        const searchResultList = await this.getDetailList(topicList);
        const csvList: any = [];
        await searchResultList.forEach((detail) => {
            const { items } = detail;
            items.forEach(async (item) => {
                const presentationData = await this.getTemplatePresentationData(item.id);
                const result = await this.checkTemplateWithStandard(presentationData);
                if (
                    result.isStandardWidth
                    && result.hasCover
                    && result.hasContents
                    && result.hasSection
                    && result.hasCtrTitleInCover
                ) {
                    csvList.push({
                        ...result,
                        id: item.id,
                        name: item.name,
                    });
                }
            });
        });

        // @ts-ignore
        window.csvList = csvList;
        return csvList;
    };

    public checkTemplateById = async (templateId: string) => {
        const presentationData = await this.getTemplatePresentationData(templateId);
        const result = await this.checkTemplateWithStandard(presentationData);
        return result;
    };

    public checkSelf = async () => {
        const presentationData = this.services.uiDataService.presentation.sdkData?.getCloneData();
        if (presentationData) {
            const result = await this.checkTemplateWithStandard(presentationData);
            return result;
        }
    };

    /**
     * 获取话题列表
     */
    public getTopicList = async (): Promise<ITopic[]> => {
        if (this.topicList.length) return this.topicList;

        const res = await getPptTemplate();
        const topicCgiList = res?.[0];
        if (topicCgiList?.length) {
            const topicList: ITopic[] = topicCgiList.filter?.((topic: ITopic) => topic.topicId && topic.style?.desc);

            topicList.unshift({
                topicId: '',
                style: {
                    desc: i18n.t('全部'),
                },
            });

            this.topicList = topicList;
            return topicList;
        }

        return [];
    };

    /**
     * 根据话题获取对应模版列表
     * @param newTopicList
     */
    public getDetailList = async (newTopicList?: ITopic[]): Promise<SearchResult[]> => {
        if (this.detailList.length) return this.detailList;

        const topicList = newTopicList?.filter?.(topic => !!topic.topicId) || [];
        const topicIds = topicList.map(topic => topic.topicId);
        const detailList = await getPptTemplateDetail(topicIds) as SearchResult[];
        const idMap = {};

        detailList.forEach((res) => {
            const { items } = res;
            const newItems = items.filter((item) => {
                const { id } = item;

                if (idMap[id]) {
                    return false;
                }

                if (IGNORE_TEMPLATE[id]) {
                    return false;
                }

                const img = this.getCoverSrc(this.getValidMeta(item.metaOfEachType));
                if (!img?.startsWith('http')) {
                    return false;
                }

                idMap[id] = true;
                return true;
            });

            res.items = newItems;
        });

        this.detailList = detailList;
        return detailList;
    };

    /**
     * 根据模版id拉取模版数据
     * 注意：这个拉取是忽略用户vip身份的，任务用户都可以拉取vip模版，用之前考虑下场景，比如预览是可以用的，但是应用场景就要考虑vip阻断
     * @param templateId
     */
    public getTemplatePresentationData = async (templateId: string): Promise<IPresentationData> => {
        const slideFileData = await fetchTemplateByIdByNonVip(templateId);
        const presentationData = convert(cloneDeep(slideFileData));
        presentationData.noteSlideMap = {};
        return presentationData;
    };

    /**
     * 获取模版封面图url
     * @param metaData
     */
    private getCoverSrc = (metaData: Partial<MetaOfTemplate>): string | undefined => metaData?.images?.pc_thumb_2x.url
        || metaData?.images?.pc_preview.url
        || metaData?.images?.mobile_thumb_2x.url;

    /**
     * 解析模版元数据
     * @param metaOfEachType
     */
    private getValidMeta = (metaOfEachType: string): Partial<MetaOfTemplate> => {
        try {
            const meta = metaOfEachType ? JSON.parse(metaOfEachType) : {};
            return meta;
        } catch (e) {
            window.log.error('getValidMeta err', e);
            return {};
        }
    };
}
